import { Formik } from 'formik';
import * as Yup from 'yup';
import { IExportFuelSalesDataDTO } from '../../resources/interfaces';
import DatePickerInput from '../form-control/DatePickerInput';
import { subDays } from 'date-fns';
import { XMarkIcon } from '@heroicons/react/20/solid';
import Button from '../form-control/Button';
import moment from 'moment';
import { INPUT_DATE_FORMAT } from '../../resources/constants';
import { exportFuelPartnersales } from '../../api/requests';
import { useState } from 'react';
import useUserAccount from '../../hooks/userAccountHook';
import SelectDropdown, { ISelectDropdownItem } from '../form-control/SelectDropdown';
import { showToast } from '../../utils/miscFunctions';

interface IExportPartnerFuelDataProps {
  onProceed: (data: IExportFuelSalesDataDTO) => void;
  onClose: () => void;
  partnerTeams: any;
}

interface IFormData {
  stationId?: string;
  from: Date;
  to: Date;
}

const ExportPartnerFuelData = (props: IExportPartnerFuelDataProps) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { user } = useUserAccount();
  const submitHandler = (values: IFormData) => {
    setSubmitting(true);

    const data = {
      stationId: values.stationId,
      from: moment(values.from).format(INPUT_DATE_FORMAT),
      to: moment(values.to).format(INPUT_DATE_FORMAT)
    };

    exportFuelPartnersales(data)
      .then((response: any) => {
        window.open(response?.url, '_blank');
        props.onProceed(data);
      })
      .catch((error: any) => {
        showToast.error(error?.description ?? 'Failed to export file. Please try again later.');
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <div className="bg-primary-main flex justify-between">
        <div className="p-[1rem] flex justify-between w-full items-center">
          <h2>Confirm Export</h2>
          <XMarkIcon
            onClick={props.onClose}
            width={'11px'}
            height={'11px'}
            className="cursor-pointer"
          />
        </div>
      </div>
      <Formik
        initialValues={{
          stationId: '',
          from: subDays(new Date(), 7),
          to: new Date()
        }}
        // validateOnBlur
        validationSchema={Yup.object().shape({
          from: Yup.date().typeError('Please pick a valid "from" date').required('Please pick a "from" date')
        })}
        onSubmit={(values) => {
          submitHandler(values);
        }}>
        {({
          values,
          errors,
          isSubmitting,
          handleSubmit,
          handleChange,
          handleBlur,
          setFieldValue,
          touched
        }) => (
          <form className="px-4 py-[30px] bg-white space-y-4" onSubmit={handleSubmit}>
            <div className="flex space-x-4 w-full justify-between">
              <DatePickerInput
                id={'from'}
                label="From"
                selected={values.from}
                onChange={(date: Date | null) => {
                  setFieldValue('from', date);
                }}
                classes="w-full"
                showYearDropdown
                allowFutureSelections={false}
                error={touched.from && errors.from !== undefined ? String(errors.from) : undefined}
                helperText={
                  touched.from && errors.from !== undefined ? String(errors.from) : undefined
                }
                dateFormat={'dd/MM/yyyy'}
              />

              <DatePickerInput
                id={'to'}
                selected={values.to}
                onChange={(date: Date | null) => {
                  setFieldValue('to', date);
                }}
                label="To"
                classes="w-full"
                showYearDropdown
                allowFutureSelections={false}
                error={touched.to && errors.to !== undefined ? String(errors.to) : undefined}
                helperText={touched.to && errors.to !== undefined ? String(errors.to) : undefined}
                dateFormat={'dd/MM/yyyy'}
              />
            </div>

            {(user.isManager() || user.isOwner()) && (
              <SelectDropdown
                name="stationId"
                label="Station"
                listSelectedValue={values.stationId}
                placeholder="Select station"
                onChange={(value: string) => {
                  setFieldValue('stationId', value);
                }}
                list={props.partnerTeams?.map(
                  (station: any) =>
                    ({
                      name: station.name,
                      value: station.id
                    } as ISelectDropdownItem)
                )}
              />
            )}

            <div className="flex justify-end w-full">
              <Button
                label="Submit"
                type="submit"
                className="bg-primary-main px-6 rounded border-none"
                loading={submitting}
                disabled={submitting}
              />
            </div>
          </form>
        )}
      </Formik>
    </>
  );
};

export default ExportPartnerFuelData;
